/**
 * FORMATO — cómo se ve un número y una fecha en toda la tienda.
 *
 * Existe para que no haya dos maneras de escribir lo mismo. Hoy el panel mezcla
 * «hace 29 minutos» con fechas sueltas, y cada pantalla formatea la plata a su
 * manera. Un tablero donde el mismo dato se ve distinto según dónde lo mires es
 * un tablero en el que uno deja de confiar.
 */

/** Monto entero en la moneda del negocio (no centavos): 1800 → RD$1,800. */
export function dinero(monto: number | null | undefined, prefijo = 'RD$'): string {
  if (monto == null) return '—';
  const signo = monto < 0 ? '−' : '';
  return `${signo}${prefijo}${Math.abs(monto).toLocaleString('en-US')}`;
}

/**
 * Un número que no se sabe se muestra como «no se sabe», nunca como cero.
 *
 * Es la regla de finanzas llevada a la pantalla: un costo sin cargar mostrado
 * como RD$0 convierte una pérdida en ganancia a la vista del dueño.
 */
export function dineroODesconocido(monto: number | null | undefined, prefijo = 'RD$'): string {
  return monto == null ? 'sin costo cargado' : dinero(monto, prefijo);
}

/** Entero con separador de miles. Para cantidades, no para plata. */
export function numero(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString('en-US');
}

/** Porcentaje con un decimal: 0.3421 → «34,2 %». */
export function porcentaje(fraccion: number | null | undefined): string {
  if (fraccion == null || !Number.isFinite(fraccion)) return '—';
  return `${(fraccion * 100).toFixed(1).replace('.', ',')} %`;
}

const MESES = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

/**
 * FECHA ABSOLUTA: «25 sep, 3:40 p. m.»
 *
 * Es lo que se muestra. «Hace 29 minutos» obliga a hacer una cuenta mental
 * para saber de cuándo es algo, y a las tres horas ya no dice nada útil: el
 * dueño quiere saber si esa venta fue ayer o el martes pasado.
 *
 * Lo relativo no se pierde: va en el `title`, para quien pase el mouse.
 */
export function fecha(iso: string | Date | null | undefined, conAnio = false): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';

  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const meridiano = h < 12 ? 'a. m.' : 'p. m.';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const anio = conAnio || d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : '';

  return `${d.getDate()} ${MESES[d.getMonth()]}${anio}, ${h12}:${m} ${meridiano}`;
}

/** Solo el día: «25 sep». Para agrupadores y ejes de gráfico. */
export function fechaCorta(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getDate()} ${MESES[d.getMonth()]}`;
}

/** Lo relativo, para el tooltip: «hace 29 minutos». */
export function haceCuanto(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';

  const seg = Math.round((Date.now() - d.getTime()) / 1000);
  const futuro = seg < 0;
  const s = Math.abs(seg);
  const di = (n: number, uno: string, varios: string) =>
    `${futuro ? 'en' : 'hace'} ${n} ${n === 1 ? uno : varios}`;

  if (s < 45) return futuro ? 'en un momento' : 'hace un momento';
  if (s < 3600) return di(Math.round(s / 60), 'minuto', 'minutos');
  if (s < 86400) return di(Math.round(s / 3600), 'hora', 'horas');
  if (s < 2592000) return di(Math.round(s / 86400), 'día', 'días');
  if (s < 31536000) return di(Math.round(s / 2592000), 'mes', 'meses');
  return di(Math.round(s / 31536000), 'año', 'años');
}

/** Días transcurridos desde una fecha. Para «días sin venderse». */
export function diasDesde(iso: string | Date | null | undefined): number | null {
  if (!iso) return null;
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

/**
 * Variación contra el período anterior.
 *
 * `null` cuando el anterior era 0: pasar de 0 a 5 no es «+∞ %», es «no había
 * con qué comparar», y mostrar un infinito en un tablero de plata es peor que
 * no mostrar nada.
 */
export function variacion(actual: number, anterior: number): number | null {
  if (anterior === 0) return null;
  return (actual - anterior) / Math.abs(anterior);
}
